import CancelIcon from '@mui/icons-material/Cancel'
import { Box, List, ListItem, Typography } from '@mui/material'
import { fileUrl } from 'api/api'
import { resetStatePayment } from 'app/slice/payment.slice'
import { notiSuccess } from 'component/notifications/notifications'
import { colors } from 'constants/theme'
import AlertModal from 'new-components/modal/alert-modal'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { routeName } from 'routes/routes-name'
import { ModalCustomProps } from '../types/payment.types'
import { toPascalCase } from '../utils/paymentUtils'

interface PaymentModalsProps {
  props: ModalCustomProps
  configModal: any
  openModal: boolean
  isOpenAlertModal: boolean
  isNhsoSaveMedicalModal: boolean
  isCheckErrorNhsoModal: boolean
  isDupicate: boolean
  checkDataError: any[]
  checkType: any[]
  dataUrl: any
  objectData: any
  currentRank: any
  newRank: any
  autoIncrement: string
  onCloseConfigModal: () => void
  onCloseAlertModal: () => void
  onCloseNhsoModal: () => void
  onCloseErrorNhsoModal: () => void
  onCloseDupicateModal: () => void
  onConfirmNhso: () => void
  onConfirmRankChange: () => void
}

export default function PaymentModals({
  props,
  configModal,
  openModal,
  isOpenAlertModal,
  isNhsoSaveMedicalModal,
  isCheckErrorNhsoModal,
  isDupicate,
  checkDataError,
  checkType,
  dataUrl,
  objectData,
  currentRank,
  newRank,
  autoIncrement,
  onCloseConfigModal,
  onCloseAlertModal,
  onCloseNhsoModal,
  onCloseErrorNhsoModal,
  onCloseDupicateModal,
  onConfirmNhso,
  onConfirmRankChange
}: PaymentModalsProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()

  return (
    <>
      {/* Config Modal */}
      <AlertModal
        title={configModal.title}
        message={configModal.message}
        isOpen={openModal}
        type={'warning'}
        handleClose={onCloseConfigModal}
        onCancel={{ onClick: onCloseConfigModal, title: t('BUTTON.ENTER') }}
        onSubmit={{ onClick: onCloseConfigModal }}
        onCloseSubmit={true}
      />

      {/* Patient Type Alert Modal */}
      <AlertModal
        isOpen={isOpenAlertModal}
        title='ยืนยันการเพิ่มประเภทผู้ป่วย'
        message='เพิ่มประเภทผู้ป่วย ผู้ป่วยอุดฟัน(ผู้ป่วยอุดฟัน) เนื่องจากตรงตามเงื่อนไข'
        type='success'
        handleClose={onCloseAlertModal}
        onCancel={{
          onClick: () => {
            onCloseAlertModal()
            if (props?.onSuccess) props.onSuccess(objectData)
            dispatch(resetStatePayment())
            window.open(`${fileUrl}/${dataUrl}`, '_blank')
            notiSuccess(t('PAYMENT.MESSAGE.SUCCESS.PAY_SUCCESS'), 'reset', null, null, 'register-page')
            history.push(routeName.payment)
          },
          title: 'ตกลง'
        }}
        onSubmit={{ onClick: () => null }}
        onCloseSubmit
      >
        <Box>
          <Typography fontSize="14px" fontWeight={600} marginTop="16px">ประเภทผู้ป่วย</Typography>
          {checkType.map((item, idx) => (
            <Box marginTop="16px" key={item.itemId || idx}>
              <Typography fontSize="14px" fontWeight={400}>{item?.patientTypeName}</Typography>
              <Typography fontSize="14px" fontWeight={400} paddingLeft="16px"> • {item?.itemName}</Typography>
            </Box>
          ))}
        </Box>
      </AlertModal>

      {/* NHSO Save Medical Modal */}
      <AlertModal
        isOpen={isNhsoSaveMedicalModal}
        handleClose={onCloseNhsoModal}
        onCancel={{ onClick: onCloseNhsoModal }}
        onSubmit={{ onClick: onConfirmNhso }}
        title='ยืนยันการส่งเบิก'
        message={'ระบบตรวจสอบรูปแบบการส่งถูกต้อง\nคุณยืนยันส่งเบิกการใช้สิทธิสปสช.ใน visit นี้ ใช่หรือไม่'}
        type='success'
      />

      {/* NHSO Error Modal */}
      <AlertModal
        isOpen={isCheckErrorNhsoModal}
        title='ไม่สามารถส่งเบิก'
        message={`ระบบไม่สามารถส่งเบิกการใช้สิทธิสปสช.เนื่องจากรูปแบบไม่ถูกต้อง\nคุณต้องแก้ไขรูปแบบให้ถูกต้องหรือเปิด visit ทั่วไปเพื่อดำเนินการชำระเงิน\nโดยไม่ใช้สิทธิสปสช.`}
        type='error'
        handleClose={onCloseErrorNhsoModal}
        onCancel={{ onClick: onCloseErrorNhsoModal, title: 'ตกลง' }}
        onSubmit={{ onClick: () => null }}
        onCloseSubmit
      >
        <Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap="8px" paddingY="16px">
            <CancelIcon style={{ color: 'red' }} />
            <Typography fontSize="14px" fontWeight={600}>หมายเหตุข้อผิดพลาด</Typography>
          </Box>
          {checkDataError.map((item, idx) => (
            <Box key={item?.statusCode || idx} display="flex" flexDirection="row" alignItems="center">
              <Typography>• {item?.statusCode} = {toPascalCase(item?.statusDesc)}</Typography>
            </Box>
          ))}
        </Box>
      </AlertModal>

      {/* Rank Change Modal */}
      <AlertModal
        isOpen={isDupicate}
        title={t('RANK.CONFIRM_MEMBER_RANK_CHANGE')}
        message={t('RANK.PATIENT_RANK_CHANGE_NOTIFICATION', { newRankName: newRank?.rankName })}
        type="success"
        onSubmit={{ onClick: () => null }}
        onCancel={{ onClick: onConfirmRankChange, title: t('BUTTON.ENTER') }}
        onCloseSubmit
        handleClose={onCloseDupicateModal}
      >
        <Box sx={{ pt: 3, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography variant="body1" sx={{ pb: 2, fontWeight: 'bold' }}>{t('RANK.PREVIOUS_MEMBER_RANK')}</Typography>
          <Typography variant="body2">{currentRank?.rankName}</Typography>
          <List sx={{ pl: 2 }}>
            <ListItem disablePadding>
              <Box sx={{ width: '5px', height: '5px', backgroundColor: colors.gray, display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '5px', marginTop: '3px', borderRadius: '50px' }}></Box>
              <Typography variant="body2">
                {autoIncrement !== 'NO_SETTING'
                  ? autoIncrement !== 'VISIT_COUNT'
                    ? t('RANK.TOTAL_SPENDING_SINCE', { total: currentRank?.totalPaid })
                    : t('RANK.VISIT_COUNT_SINCE', { total: currentRank?.serviceVisits })
                  : <></>}
              </Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ pb: 2, fontWeight: 'bold' }}>{t('RANK.NEW_MEMBER_RANK')}</Typography>
          <Typography variant="body2">{newRank?.rankName}</Typography>
          <List sx={{ pl: 2 }}>
            <ListItem disablePadding>
              <Box sx={{ width: '5px', height: '5px', backgroundColor: colors.gray, display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '5px', marginTop: '3px', borderRadius: '50px' }}></Box>
              <Typography variant="body2">
                {autoIncrement !== 'NO_SETTING'
                  ? autoIncrement !== 'VISIT_COUNT'
                    ? t('RANK.NEW_TOTAL_SPENDING', { newTotal: newRank?.totalPaid })
                    : t('RANK.NEW_VISIT_COUNT', { newTotal: newRank?.serviceVisits })
                  : <></>}
              </Typography>
            </ListItem>
          </List>
        </Box>
      </AlertModal>
    </>
  )
}
